// useActiveTenant — reads the active tenant context from the JWT-backed
// helpers added in mig 232. Tells the UI:
//   - kind          — PACKHOUSE / FARM / PLATFORM (drives Dashboard shape)
//   - growerIds     — UUIDs of growers linked to this tenant (cross-tenant
//                     reads at KF49 light up for these growers)
//   - displayName   — for the Dashboard header
//
// The helpers run as SECURITY DEFINER so they bypass RLS to read tenants
// and growers — safe because they only return info about the calling
// user's own tenant.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { decodeStoredClaims, decodeJwtPayload, LAST_TENANT_KEY } from '@/lib/offlineSession'
import { planCappedModules } from '@/lib/planModules'

// Mirrors the tenants_kind_check CHECK constraint — keep the two in step.
// PAYROLL (mig 20260807140000) holds workers + payroll for one or more legal
// employers and owns no operational catalogue; see PAYROLL_TENANT_MODULES.
// SECURITY (mig 20261113000000) is a security company (BigSecurity).
export type TenantKind   = 'PACKHOUSE' | 'FARM' | 'PLATFORM' | 'AGENT' | 'EXPORTER' | 'SUPPLIER' | 'BUYER' | 'TRANSPORTER' | 'HARVEST' | 'PAYROLL' | 'SECURITY'
export type TenantStatus = 'TRIAL' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED'

export interface ActiveTenant {
  kind:        TenantKind
  growerIds:   string[]
  displayName: string | null
  tenantId:    string | null
  // Phase 3c-1: tenant lifecycle status from the JWT. SUSPENDED / ARCHIVED
  // tenants don't get an active_tenant_id claim (mig 241), so RLS already
  // blocks every read — but we still surface the status so the app can
  // render an explanatory lockout page rather than a broken empty UI.
  status:      TenantStatus | null
  // Mig 330: when this tenant is AGENT-kind, the franchise it belongs to
  // (NULL = independent agent). Drives the gFranchise route gate.
  franchiseId: string | null
  // Effective capability bundle (mig 469). plan_tiers.default_capabilities
  // ‖ tenants.capabilities — tenant overrides win. Use `useTenantCapability`
  // below to gate features behind a key. NULL keys default to false.
  capabilities: Record<string, unknown>
  // Per-tenant module entitlements (tenants.enabled_modules, mig 195).
  // Empty / null = uncapped (legacy + default). When non-empty, this is
  // the billing-level subscription cap — `hasModule` intersects user
  // permissions with this list. Set from the platform-admin app.
  enabledModules: string[] | null
  // The tenant's plan (get_tenant_plan, mig 20261057700000). `enabledModules`
  // above is ALREADY narrowed by planExcludedModules (planModules.ts), so
  // module gates need nothing extra; these two are for the in-page cases —
  // Labour's pay tabs ('payroll'), add-ons a plan includes (useAddonState).
  // Optional: an offline snapshot written before this field carries none.
  planExcludedModules?: string[]
  planIncludedAddons?: string[]
  // tenants.slug — the pilot gate in hasModule (PILOT_MODULE_TENANTS) matches
  // on it. Optional because an offline snapshot written before this field
  // existed carries none; a pilot module then stays hidden until the next
  // online resolve (fail-closed). Nothing else should branch on the slug —
  // tenant-specific behaviour belongs in capabilities / config rows.
  slug?: string | null
}

// The tenant's plan lists (get_tenant_plan, mig 20261057700000). A failed read
// reuses THIS tenant's snapshot, else none: showing a Compliance tenant a pay
// tab for a beat is the safe direction (the server refuses the run); hiding
// Standard's modules on a blip is not. Never throws — a bundle that ships
// ahead of the migration reads as Standard.
async function readTenantPlan(tenantId: string): Promise<{ planExcludedModules: string[]; planIncludedAddons: string[] }> {
  const { data, error } = await supabase.rpc('get_tenant_plan', { p_tenant_id: tenantId })
  if (error || !data) {
    const snap = readLastActiveTenant()
    return snap && snap.tenantId === tenantId
      ? { planExcludedModules: snap.planExcludedModules ?? [], planIncludedAddons: snap.planIncludedAddons ?? [] }
      : { planExcludedModules: [], planIncludedAddons: [] }
  }
  const p = data as { excluded_modules?: string[] | null; included_addons?: string[] | null }
  return { planExcludedModules: p.excluded_modules ?? [], planIncludedAddons: p.included_addons ?? [] }
}

// Last successfully-resolved tenant, kept in a dedicated long-lived localStorage
// key (NOT the 24h-maxAge React Query persist cache) so a field device that
// cold-boots offline — or has been offline for days — still knows its tenant
// KIND and lands on the right dashboard. Keyed on the JWT's active_tenant_id at
// read time so a stale snapshot for a different tenant is never returned.
function readLastActiveTenant(): (ActiveTenant & { _userId?: string | null }) | null {
  try {
    const raw = localStorage.getItem(LAST_TENANT_KEY)
    return raw ? (JSON.parse(raw) as ActiveTenant & { _userId?: string | null }) : null
  } catch { return null }
}
// F1 (offline audit) — persist the USER (JWT sub) the snapshot was resolved for.
// The key is device-global, so the cold-boot fallback below must refuse to reuse
// it for a DIFFERENT user on a shared tablet. Mirrors useCurrentUser's
// readLastMe / `last.id === userId` guard.
function writeLastActiveTenant(t: ActiveTenant, userId: string | null): void {
  try { localStorage.setItem(LAST_TENANT_KEY, JSON.stringify({ ...t, _userId: userId })) } catch { /* quota/unavailable — best effort */ }
}

export function useActiveTenant() {
  return useQuery<ActiveTenant>({
    queryKey: ['active-tenant'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      // Resolve the session FIRST, then fire the auth-gated RPCs. On a cold
      // boot (a hard refresh) supabase-js is still hydrating its session from
      // storage; firing current_tenant_kind in parallel with getSession() can
      // send it before the token is attached → 401, kindRes.error, and (with
      // no snapshot match) every tenant-kind-gated route bounces to home.
      // Awaiting getSession() means the token is loaded before the RPCs go out.
      // getSession() acquires the GoTrue navigator lock during cold-boot auth
      // init; if that throws/contends the whole queryFn would throw → tenant
      // resolves to undefined → the gate bounces home BEFORE the snapshot
      // fallback below ever runs (the stack pointed straight at _acquireLock /
      // _emitInitialSession). Make it non-throwing so we always reach the
      // fallback. Same for the RPCs — supabase-js already resolves their errors
      // as { data:null, error } rather than throwing, but guard the await chain.
      const claimsRes = await supabase.auth.getSession()
        .catch(() => ({ data: { session: null } } as Awaited<ReturnType<typeof supabase.auth.getSession>>))
      const [kindRes, growerRes] = await Promise.all([
        supabase.rpc('current_tenant_kind').then(r => r, (e: unknown) => ({ data: null, error: e as Error })),
        supabase.rpc('current_tenant_grower_ids').then(r => r, (e: unknown) => ({ data: null, error: e as Error })),
      ])

      // Decode the JWT once for both active_tenant_id and tenant_status.
      // Offline, the access token may be expired → getSession() returns null;
      // recover the claims from the stored token so the active tenant + status
      // survive offline (the kind/displayName/etc. come from the snapshot below).
      const liveToken    = claimsRes.data?.session?.access_token
      const livePayload  = liveToken ? decodeJwtPayload(liveToken) : null
      const storedPayload = decodeStoredClaims()?.raw ?? null
      // A valid active_tenant_id claim, or null. Also normalises the literal
      // strings 'undefined'/'null' a bad server-side mint could emit (the
      // "invalid input syntax for type uuid: \"undefined\"" Sentry, 2026-06-03)
      // and the empty string.
      const validTid = (p: Record<string, unknown> | null): string | null => {
        const t = p?.active_tenant_id
        return (typeof t === 'string' && t !== 'undefined' && t !== 'null' && t.length > 0) ? t : null
      }
      // Prefer whichever token actually CARRIES active_tenant_id. On a cold boot
      // getSession() can hand back a just-refreshed token that momentarily lacks
      // the custom claims while the stored token still has them (and vice-versa).
      // The old `livePayload ?? storedRaw` kept a claim-less live payload because
      // it's a truthy object — shadowing the good stored token → tenantId null →
      // every FARM/PACKHOUSE route bounced to home on refresh.
      const payload  = validTid(livePayload)   ? livePayload
                     : validTid(storedPayload) ? storedPayload
                     : (livePayload ?? storedPayload)
      const tenantId = validTid(payload)
      // tenant_status is decoded INDEPENDENTLY of the active_tenant_id-based
      // payload pick above, always preferring the LIVE (freshest) token. A
      // tenant going ACTIVE→SUSPENDED mints a token with tenant_status=SUSPENDED
      // but active_tenant_id=NULL (mig 241) — so the active_tenant_id pick would
      // choose the STALE stored token (still ACTIVE) and the suspended user would
      // slip past TenantStatusGate. Reading status from the live token first
      // keeps the lockout honest (RLS already blocks their reads either way).
      const status = (livePayload?.tenant_status ?? storedPayload?.tenant_status ?? null) as TenantStatus | null
      // mig 710: when the auth hook emits tenant_kind, the active tenant KIND
      // rides in the JWT — authoritative (it always agrees with active_tenant_id)
      // and available offline straight from the (live or stored) token, no RPC,
      // no snapshot. Prefer it; fall back to the current_tenant_kind RPC for
      // tokens minted before the claim existed.
      const claimKind = (typeof payload?.tenant_kind === 'string' ? payload.tenant_kind : null) as TenantKind | null
      // F1 — the current user id (JWT sub), for the shared-tablet snapshot guard
      // below. `sub` rides every token even when active_tenant_id is momentarily
      // absent — exactly the cold-boot state the fallback handles.
      const sub = payload?.sub
      const currentUserId = (typeof sub === 'string' && sub.length > 0) ? sub : null

      // COLD-BOOT RESILIENCE. On a hard refresh the active_tenant_id claim can
      // be momentarily unresolvable — a just-refreshed token can come back
      // without the custom claims, and the raw stored token can be mid-rewrite —
      // which lands a tenant-less result (tenantId null, kind falsy) and bounces
      // every RequireTenantKind route to home (the reported bug, KF49 2026-06-11,
      // verified via the [AT-DIAG v3] line: tenantId null with a healthy snapshot
      // on disk). When that happens but we hold a last-known tenant snapshot,
      // reuse it. No session guard: the snapshot itself is proof of a prior
      // signed-in resolution and is CLEARED on sign-out (clearOfflineIdentity),
      // so a genuinely signed-out user has none and still falls through. RLS
      // scopes every read/write to the JWT's real tenant regardless, so this only
      // steadies client-side routing — it never widens access.
      const snapshot = readLastActiveTenant()
      // Fire the fallback on the snapshot's KIND, not its tenantId — routing only
      // needs the kind, and this fires even if an old/partial snapshot lacks a
      // tenantId. Carry the CURRENT token's status over the snapshot's so a
      // tenant suspended since the snapshot still hits the lockout (mirrors the
      // kindRes.error fallback below); status falls back to the snapshot's only
      // when the live token carries none (a glitch, not a suspension).
      //
      // F1 (offline audit) — GUARD on the user. The snapshot key is device-global,
      // so on a SHARED tablet reusing it for a DIFFERENT user (whose token
      // momentarily lacks active_tenant_id) would populate B's module gates /
      // grower visibility / capabilities from A's leftover snapshot. RLS still
      // scopes server reads to B's real JWT (no exfiltration), but the client UI
      // would be A's. Only reuse when the snapshot was resolved for THIS user
      // (JWT sub); a snapshot written before this field is skipped (fail-closed)
      // until the next successful resolve rewrites it.
      if (!tenantId && snapshot?.kind != null && currentUserId != null && snapshot._userId === currentUserId) {
        return { ...snapshot, kind: claimKind ?? snapshot.kind, status: status ?? snapshot.status }
      }

      // OFFLINE-SAFE KIND. current_tenant_kind is a NETWORK rpc, and supabase-js
      // resolves a failed fetch as { data: null, error } (it does NOT throw), so
      // the old `kindRes.data ?? 'PACKHOUSE'` silently flipped an offline FARM
      // scout onto the PACKHOUSE dashboard — and, because the query still
      // "succeeded", overwrote the good cached FARM value. So only trust a
      // SUCCESSFUL rpc. On error (offline / cold boot in the field) prefer the
      // JWT claim, else reuse the last-known snapshot for THIS tenant; if there's
      // none, throw so React Query keeps any cached data rather than asserting
      // PACKHOUSE. A genuine rpc success returning null still means PACKHOUSE.
      if (kindRes.error) {
        const last = readLastActiveTenant()
        if (last && last.tenantId === tenantId) return { ...last, kind: claimKind ?? last.kind, tenantId, status }
        // No snapshot: even with the JWT kind we lack growerIds/capabilities/
        // enabledModules, so throw and let React Query keep any cached value.
        throw kindRes.error
      }
      const kind = (claimKind ?? kindRes.data ?? 'PACKHOUSE') as TenantKind

      // Look up display name + franchise_id + capabilities when we have
      // a tenant id; tolerated as null if the user is platform-admin or
      // pre-claim. Mig 469 layered tier defaults underneath the per-tenant
      // overrides via get_effective_capabilities — we read THAT, not the
      // raw tenants.capabilities, so a feature listed only in the tier
      // catalog still resolves to its catalog default. Falls back to the
      // raw column if the RPC isn't available (mig 469 not yet applied).
      let displayName: string | null = null
      let franchiseId: string | null = null
      let capabilities: Record<string, unknown> = {}
      let enabledModules: string[] | null = null
      let slug: string | null = null
      let planExcludedModules: string[] = []
      let planIncludedAddons: string[] = []
      if (tenantId) {
        // Symmetric with kindRes above and growerRes below: this file refuses
        // to degrade-and-persist twice already, and the tenants SELECT was the
        // one read left unguarded. Dropping its error blanked displayName,
        // franchiseId, capabilities AND enabled_modules, and that degraded
        // object is then written as the offline snapshot — so the next offline
        // boot comes up with no modules enabled. Realistic trigger: a bundle
        // shipping ahead of a migration that adds a selected column.
        const { data, error: tenantErr } = await supabase.from('tenants')
          .select('display_name, franchise_id, capabilities, enabled_modules, slug').eq('tenant_id', tenantId).maybeSingle()
        if (tenantErr) throw tenantErr
        displayName    = data?.display_name ?? null
        franchiseId    = (data as any)?.franchise_id ?? null
        enabledModules = ((data as any)?.enabled_modules ?? null) as string[] | null
        slug           = ((data as any)?.slug ?? null) as string | null
        const fallback = ((data as any)?.capabilities ?? {}) as Record<string, unknown>
        const { data: eff, error: effErr } = await supabase.rpc('get_effective_capabilities', { p_tenant_id: tenantId })
        capabilities = (effErr || !eff) ? fallback : (eff as Record<string, unknown>)
        const plan = await readTenantPlan(tenantId)
        planExcludedModules = plan.planExcludedModules
        planIncludedAddons  = plan.planIncludedAddons
        enabledModules = planCappedModules(enabledModules, planExcludedModules)
      }

      // Symmetric with kindRes above: a failed current_tenant_grower_ids RPC
      // must NOT degrade growerIds to [] and then overwrite the good cached
      // snapshot — that silently strips cross-tenant grower visibility on the
      // next offline boot. On error reuse the snapshot's growerIds for THIS
      // tenant; on success use the fresh list.
      const growerSnap = growerRes.error ? readLastActiveTenant() : null
      const growerIds = (growerRes.error
        ? (growerSnap && growerSnap.tenantId === tenantId ? growerSnap.growerIds : [])
        : (growerRes.data ?? [])) as string[]

      const result: ActiveTenant = {
        kind,
        growerIds,
        displayName,
        tenantId,
        status,
        franchiseId,
        capabilities,
        enabledModules,
        planExcludedModules,
        planIncludedAddons,
        slug,
      }
      // Snapshot for offline cold boots (see readLastActiveTenant). Only when we
      // have a tenant id AND both RPCs succeeded — never persist a partial
      // (empty-grower) result over a good snapshot.
      if (tenantId && !growerRes.error) writeLastActiveTenant(result, currentUserId)
      return result
    },
  })
}

// useTenantCapability — boolean read of a feature-flag key on the
// active tenant's `tenants.capabilities` JSONB. Mig 235 added the
// column; mig 437-era code wires it up to the frontend.
//
//   const isExperimental = useTenantCapability('experimental_qc')
//   if (!isExperimental) return null
//
// Falsy values (undefined, null, false, 0, '') resolve to false.
// Truthy primitives or objects resolve to true. Useful for rolling
// out a feature to a single design-partner tenant before everyone.
export function useTenantCapability(key: string): boolean {
  const { data: tenant } = useActiveTenant()
  const v = tenant?.capabilities?.[key]
  return v !== undefined && v !== null && v !== false && v !== 0 && v !== ''
}
